import * as PIXI from "pixi.js";
import FXManager from "../../Engine/Effects/FXManager";
import { EASING_FNS, evaluatePath } from "../../Engine/Collection/BezierPath";
import { FEATURES_CONFIG } from "../../featuresConfig";
import { soundManager } from "../../SoundManager";
import { SOUND_NAME } from "../../sounds";

/**
 * coinToLogoTrail — a landed coin throws a fire trail into the Pot.
 *
 * Cleopatra's Fire GDD §5.1: every coin that lands sends a trail into the fire
 * cup on the logo, BEFORE any line win shows.
 *
 * The trail is Zeus's `trail_coin` (effects/trail/particle_trail.json —
 * soft_glow, additive, dense), flown the way Zeus's value traveler flew it: a
 * point moves along a Bézier, the emitter follows it. Tuned for fire
 * (FEATURES_CONFIG.LOGO_POT):
 *
 *   size    the biggest particle is TRAIL_SIZE × the symbol height, worked out
 *           per trail from the live size — so it follows every resize. The
 *           particle scale ramp and the spawn spread are scaled together, so
 *           the trail gets wider as well as bigger. NOT through emitter.init's
 *           scaleMod: RevoltFX multiplies particle LIFE by it too, which would
 *           stretch the tail with the screen. One cached settings clone per
 *           screen size (the factor is rounded).
 *   tint    TRAIL_TINT_START → TRAIL_TINT_END, a hot head burning out to deep
 *           orange-red.
 *   budget  TRAIL_SPAWN_COUNT particles per spawn, at most TRAIL_SPAWN_RATE spawns a
 *           second — whatever the screen's refresh rate. As authored,
 *           trail_coin spawned 30 EVERY frame (its 0.01 s gap is shorter than
 *           a frame), so a 120 Hz screen drew twice the particles of a 60 Hz
 *           one. The glows are big and overlap, so far fewer read the same.
 *   arc     ONE even arc, like a throw: a quadratic whose peak is halfway
 *           across, TRAIL_ARC_HEIGHT × the horizontal distance (+
 *           TRAIL_ARC_LIFT symbol heights) above the higher end. Scaled with
 *           the distance, so every trail has the same shape and comes DOWN
 *           into the bowl at the same angle, near or far. The arc's top is
 *           held on screen (a quadratic peaks halfway to its control).
 *           The target: the logo's centre across, at its fire bone's height
 *           (getLogoTarget); TRAIL_TARGET_OFFSET_Y fine-tunes it.
 *
 * ── NON-BLOCKING, never cancelled ──
 *
 * Nothing in the game waits for a trail — not the next spin, not the line
 * wins, at any speed (Vladimir, 2026-09-28: "non blocking feature like
 * lightning"). A trail that was shot flies to the logo and lands its hit
 * whatever happens to the spin that shot it. Only a destroyed layer (the room
 * unmounting) ends one early.
 */

const EMITTER = "trail_coin";
// trail_coin as authored (particle_trail.json): soft_glow.png is 200 px, the
// scale ramps 0.1–0.2 → 0.3–0.5, spawn radius 7.
const NATIVE = {
  PARTICLE_MAX_PX: 200 * 0.5,
  SCALE_START: [0.1, 0.2],
  SCALE_END: [0.3, 0.5],
  SPAWN_RADIUS: 7,
};

const _point = new PIXI.Point();

const cfg = () => FEATURES_CONFIG.LOGO_POT || {};



/**
 * @param {object} p
 * @param {{trailContainer: PIXI.Container, symbolHeight: number}} p.layer
 *        the room's trail layer: drawn into, and its live symbol height sizes
 *        the fire. Base game: its trail animator (LevelChargingAnimator, which
 *        carries both). Free spins: freeSpinsCoinTrailRef.
 * @param {PIXI.DisplayObject} p.from   the coin's overlay cell (centre)
 * @param {{x:number,y:number}|null} p.target  the logo's centre, GLOBAL space
 * @param {Function} [p.onArrival]      the logo's hit
 * @param {PIXI.DisplayObject} [p.carry] rides the trail's head the whole way
 *        (the H&W End Collector's flying coin value) — the same path, the same
 *        speed as the trail itself. Destroyed when the trail finishes.
 * @param {Function} [p.onProgress]  called every frame with the head's eased
 *        progress, 0 -> 1 (the flying value grows with it)
 */
export const fireCoinToLogo = ({
  layer,
  from,
  target,
  onArrival,
  carry = null,
  onProgress = null,
}) => {
  const container = layer?.trailContainer;
  if (!container || container.destroyed || !from || from.destroyed || !target) return;
  const c = cfg();

  _point.set(0, 0);
  const startG = from.toGlobal(_point);
  const start = container.toLocal(startG);
  const startPos = { x: start.x, y: start.y };
  // The symbol height in THIS container's units: a layer inside the scene
  // spine is scaled with the scene, so the screen size is divided back out.
  const wt = container.worldTransform;
  const worldScale = Math.hypot(wt.a, wt.b) || 1;
  const symbolHeight = (layer.symbolHeight || 0) / worldScale;
  _point.set(target.x, target.y);
  const end = container.toLocal(_point);
  // Fine-tune up from the fire bone (0 = the fire itself).
  const endPos = {
    x: end.x,
    y: end.y - (c.TRAIL_TARGET_OFFSET_Y ?? 0) * symbolHeight,
  };
  // One even arc: peak halfway across, its height scaled with the distance
  // so near and far trails share one shape and one landing angle.
  const lift =
    (c.TRAIL_ARC_HEIGHT ?? 0.5) * Math.abs(startPos.x - endPos.x) +
    (c.TRAIL_ARC_LIFT ?? 0) * symbolHeight;
  let controlY = Math.min(startPos.y, endPos.y) - lift;
  // The arc's TOP stays on screen — the logo sits near the top in landscape.
  // A quadratic's highest point is (s·e − c²) / (s − 2c + e), which is ABOVE
  // its halfway point whenever the ends differ in height; keeping it at or
  // below the line T needs c ≥ T − √((s − T)(e − T)).
  _point.set(0, 0);
  const screenTop =
    container.toLocal(_point).y + (c.TRAIL_TOP_MARGIN ?? 0.15) * symbolHeight;
  const below = (startPos.y - screenTop) * (endPos.y - screenTop);
  const minControlY = below > 0 ? screenTop - Math.sqrt(below) : screenTop;
  controlY = Math.max(controlY, minControlY);
  const path = {
    start: startPos,
    control: { x: (startPos.x + endPos.x) / 2, y: controlY },
    end: endPos,
  };

  const wrapper = new PIXI.Container();
  wrapper.name = "coin_to_logo_trail";
  wrapper.position.set(startPos.x, startPos.y);
  wrapper.zIndex = 200;
  container.addChild(wrapper);
  if (carry) wrapper.addChild(carry);

  // Rounded, so a resize makes one new settings clone, not one per trail.
  const k =
    Math.round((((c.TRAIL_SIZE ?? 0.3) * symbolHeight) / NATIVE.PARTICLE_MAX_PX) * 100) /
      100 || 1;
  // The gap between spawns. RevoltFX times it with Date.now() in whole ms, so
  // a 60 Hz frame reads 16 or 17 ms — a gap of exactly 1/60 s (16.67) would
  // skip every 16 ms frame and halve the trail. 10% slack: every 60 Hz frame
  // still spawns, a 120 Hz screen spawns every other frame.
  const spawnGap = 0.9 / (c.TRAIL_SPAWN_RATE ?? 60);
  const emitter = FXManager.emitTrail(container, wrapper, {
    emitterName: EMITTER,
    particleLife: c.TRAIL_PARTICLE_LIFE ?? 0.2,
    spawnRadius: NATIVE.SPAWN_RADIUS * k,
    emitterOverrides: {
      spawnCountMin: c.TRAIL_SPAWN_COUNT ?? 10,
      spawnCountMax: c.TRAIL_SPAWN_COUNT ?? 10,
      spawnFrequencyMin: spawnGap,
      spawnFrequencyMax: spawnGap,
    },
    // The fireball's shape (LOGO_POT.TRAIL_HEAD_SCALE / _TAIL_SCALE): born
    // big at the head, shrinking behind it — a ball-like tip and a pointed
    // tail. Was NATIVE.SCALE_START -> SCALE_END (growing: fat at the tail).
    particleOverrides: {
      scaleStartMin: (c.TRAIL_HEAD_SCALE?.[0] ?? NATIVE.SCALE_END[0]) * k,
      scaleStartMax: (c.TRAIL_HEAD_SCALE?.[1] ?? NATIVE.SCALE_END[1]) * k,
      scaleEndMin: (c.TRAIL_TAIL_SCALE?.[0] ?? NATIVE.SCALE_START[0]) * k,
      scaleEndMax: (c.TRAIL_TAIL_SCALE?.[1] ?? NATIVE.SCALE_START[1]) * k,
      tintStart: c.TRAIL_TINT_START ?? 0xfff1a8,
      tintEnd: c.TRAIL_TINT_END ?? 0xff3c00,
    },
  });
  soundManager.playOncePerFrame(SOUND_NAME.trail_sound);

  const duration = c.TRAIL_DURATION_MS ?? 350;
  const ease = EASING_FNS.easeInOut;
  let elapsed = 0;

  const finish = (arrived) => {
    PIXI.Ticker.shared.remove(tick);
    if (emitter) {
      emitter.target = null;
      FXManager.stopTrail(emitter); // the particles already out burn off
    }
    if (carry && !carry.destroyed) {
      carry.parent?.removeChild(carry);
      carry.destroy();
    }
    if (!wrapper.destroyed) wrapper.destroy();
    if (arrived) onArrival?.();
  };
  const tick = () => {
    if (container.destroyed || wrapper.destroyed) {
      finish(false);
      return;
    }
    elapsed += PIXI.Ticker.shared.deltaMS;
    const t = Math.min(1, elapsed / duration);
    const progress = ease(t);
    const pos = evaluatePath(path, progress);
    wrapper.position.set(pos.x, pos.y);
    onProgress?.(progress);
    if (t >= 1) finish(true);
  };
  PIXI.Ticker.shared.add(tick);
};
